import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
// Local bearer attacher — avoids pulling `@supabase/supabase-js` (Realtime,
// Phoenix, WebSocket) into the main client bundle. See src/lib/auth-attacher-lite.ts.
import { attachSupabaseAuthLite } from "@/lib/auth-attacher-lite";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Paths that must remain iframe-embeddable (widgets/embeds we ship).
 * These skip X-Frame-Options and get a permissive frame-ancestors CSP.
 */
function isEmbeddablePath(pathname: string): boolean {
  return pathname.startsWith("/embed");
}

/**
 * Content types that get HTML-tier security headers. JSON APIs and static
 * XML/TXT (sitemap, rss, llms.txt) don't need CSP/COOP/XFO.
 */
function isHtmlLikeResponse(response: Response): boolean {
  const ct = response.headers.get("content-type") ?? "";
  return ct.includes("text/html") || ct === "";
}

/**
 * Adds baseline security response headers on HTML SSR responses.
 *
 * - HSTS: force HTTPS for 2 years (safe: mapobserver.com is HTTPS-only)
 * - Referrer-Policy, X-Content-Type-Options, Permissions-Policy: hardening
 * - COOP: isolate top-level browsing context
 * - X-Frame-Options + CSP frame-ancestors: mitigate clickjacking
 * - CSP is emitted in Report-Only mode so we can tune before enforcing
 *
 * Trusted Types is intentionally NOT enforced — react-markdown and other
 * third-party libs use innerHTML paths that would break under enforcement.
 */
const securityHeadersMiddleware = createMiddleware().server(async ({ next, request }) => {
  const response = await next();
  const res = (response as unknown as { response?: Response }).response ?? (response as unknown as Response);
  if (!(res instanceof Response)) return response;

  if (!isHtmlLikeResponse(res)) return response;

  const url = new URL(request.url);
  const embeddable = isEmbeddablePath(url.pathname);

  const csp = [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline'",
    "style-src 'self' 'unsafe-inline'",
    "img-src 'self' data: blob: https:",
    "font-src 'self' data:",
    "connect-src 'self' https://*.supabase.co wss://*.supabase.co https://connector-gateway.lovable.dev",
    "media-src 'self' https:",
    embeddable ? "frame-ancestors *" : "frame-ancestors 'self'",
    "base-uri 'self'",
    "form-action 'self'",
  ].join("; ");

  const headers = new Headers(res.headers);
  headers.set("Strict-Transport-Security", "max-age=63072000; includeSubDomains; preload");
  headers.set("X-Content-Type-Options", "nosniff");
  headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
  headers.set("Cross-Origin-Opener-Policy", "same-origin");
  headers.set(
    "Permissions-Policy",
    "geolocation=(), microphone=(), camera=(), interest-cohort=(), payment=()",
  );
  if (!embeddable) headers.set("X-Frame-Options", "SAMEORIGIN");
  // Enforcing after a report-only burn-in period. Directives allow the
  // inline SSR scripts TanStack Start emits and Tailwind's inline styles.
  headers.set("Content-Security-Policy", csp);

  return new Response(res.body, {
    status: res.status,
    statusText: res.statusText,
    headers,
  });
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuthLite],
  requestMiddleware: [errorMiddleware, securityHeadersMiddleware],
}));

